import {AttrsSchema, Component} from 'panel';
import isEqual from 'lodash/isEqual';
import {Point} from 'highcharts';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {LineStyle} from 'common/charts/visualization/line-chart/enums';
import {CurrentProject} from 'common/report/model-resources/projects';
import {
  getLiftBaselineSegmentRawHead,
  getUiRowsDataFromParsed,
  mapToTrendDataRows,
} from 'common/report/retention/util/parsing';
import {RetentionQueryResponse, SegmentKeyToPath} from 'common/report/retention/models/types';
import {FormattedRow, LiftRow, RawCell, RetentionBookmarkParams} from 'common/report/retention/util/types';
import {IChartDataItem} from 'common/charts/visualization/line-chart/types';
import {htmlEncodeString} from 'common/util';
import {ChartType, HIGHCHARTS_LINKED_ID_SUFFIX, REPORT_LIMITS, ReportName} from 'common/report/constants';
import {flattenQueryResponse, retentionResponseHasData} from 'common/report/retention/util/request';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {formatPercent, getBookmarkWithAppliedDefaults, getSegmentColumnSpecs} from 'common/report/retention/util';
import {
  formatDataTableNumber,
  getTrendDataTableColumnGroups,
  getTrendDataTableRow,
} from 'common/components/data-table/trend-data/utils';
import {LiftType} from 'common/report/util/lift/types';
import {getLiftComparisonValues} from 'common/report/util/lift';
import {RetentionBookmarkParamsAndResults} from 'common/report/retention/charts/types';
import {PopoverCallback} from 'common/charts/types';
import {deselectPoint, selectPoint} from 'common/report/util/highcharts';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';

import {percentOrAbsoluteLabel} from '../retention-curve-chart/line-chart-format';
import {getToolTipSections} from './utils';
import css from './index.inline.styl';
import {template} from './template';

export type RetentionTrendsChartAttrs = {
  'project-timezone'?: string;
  'utc-offset'?: number;
  'show-annotations'?: boolean;
  'limit-displayed-segments'?: boolean;
  'segment-display-limit'?: number;
};

type State = {
  segmentRows: FormattedRow[];
  comparisonSegmentRowsMapped: Map<string, FormattedRow>;
  liftSegmentRowsMapped: Map<string, LiftRow>;
  truncatedSegmentDisplayLookup?: Map<string, string>;
  segmentKeyToPath?: SegmentKeyToPath;
  popoverStyles?: {
    visibility?: string;
    left?: string;
    top?: string;
  } | null;
  popoverSelection?: Point | null;
};

export type RetentionTrendsChartProps = {
  paramsAndResults: RetentionBookmarkParamsAndResults;
  popoverCallback?: PopoverCallback;
};
export class RetentionTrendsChart extends Component<State, RetentionTrendsChartAttrs, any, any, ChartsContextRegistry> {
  private _bookmarkParams: RetentionBookmarkParams;
  private _data: RetentionQueryResponse;

  static get attrsSchema(): AttrsSchema<RetentionTrendsChartAttrs> {
    return {
      'project-timezone': `string`,
      'utc-offset': `number`,
      'show-annotations': `boolean`,
      'limit-displayed-segments': `boolean`,
      'segment-display-limit': `number`,
    };
  }

  public popoverCallback: PopoverCallback | null = null;

  // eslint-disable-next-line sonarjs/cognitive-complexity
  get config() {
    return {
      useShadowDom: true,
      css,
      template,
      contexts: [`segmentFilterController` as const, `currentProject` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController(),
      },
      defaultState: {
        segmentRows: [],
        comparisonSegmentRowsMapped: new Map(),
        liftSegmentRowsMapped: new Map(),
      },
      helpers: {
        hasResults: () => retentionResponseHasData(this._data),

        formatTrendsData: (): IChartDataItem[] => {
          const {segmentKeyToPath} = this.state;
          const bookmarkParams = this._bookmarkParams;
          return this.state.segmentRows
            .filter((row) => this.segmentFilterController.isItemSelected(0, row.raw_head))
            .flatMap((segmentRow) => {
              const dateLabels = segmentRow.data.map((row) => row.date.valueOf());
              const comparisonRow = this.state.comparisonSegmentRowsMapped.get(segmentRow.raw_head);
              const liftRow = this.state.liftSegmentRowsMapped.get(segmentRow.raw_head);
              if (liftRow) {
                return [getSegmentDataForChart(liftRow, dateLabels)];
              } else {
                const segmentChartItems = [getSegmentDataForChart(segmentRow, dateLabels)];
                if (comparisonRow) {
                  segmentChartItems.push(getSegmentDataForChart(comparisonRow, dateLabels, true));
                }
                return segmentChartItems;
              }
            });

          function getSegmentDataForChart(segmentRow, dateLabels, isComparisonRow = false) {
            const path = segmentKeyToPath ? segmentKeyToPath[segmentRow.head] : segmentRow.head;
            const id = isComparisonRow ? `${segmentRow.raw_head}${HIGHCHARTS_LINKED_ID_SUFFIX}` : segmentRow.raw_head;
            const linkedTo = isComparisonRow ? segmentRow.raw_head : undefined;

            return {
              id,
              ...(linkedTo ? {linkedTo} : {}),
              name: segmentRow.head as string,
              data: dateLabels.map((date, ind) => {
                const data = segmentRow.data[ind];
                return [date, bookmarkParams.percentage_view ? data.percent : data.value];
              }),
              color: segmentRow.selection_color,
              incompleteLength:
                segmentRow.data.length - (segmentRow.data.findIndex((c) => c.incomplete) || segmentRow.data.length),
              dashStyle: isComparisonRow ? LineStyle.Comparison : null,
              path,
            };
          }
        },

        isEndDateIncomplete: () => {
          const row = this.getVisibleRow(0);

          if (!row) return false;
          if (row.data.length < 2) return false;

          return row.data[row.data.length - 1].incomplete;
        },

        getProjectTimezone: () => this.attr(`project-timezone`) || this.getCurrentProject()?.timezone,

        getUtcOffset: () => this.attr(`utc-offset`) || this.getCurrentProject()?.utcOffset,

        getTimeUnit: () => this._bookmarkParams.date_unit,

        getYAxisOptions: () => {
          return this._bookmarkParams.percentage_view ? {maxPadding: 0} : {};
        },

        getLineChartProps: () => {
          return {
            chartData: this.helpers.formatTrendsData(),
            formatYAxisLabel: percentOrAbsoluteLabel(this._bookmarkParams),
            formatTooltip: this.helpers.getTrendsChartTooltip(),
            onHighChartClick: this.popoverCallback && this.handlePointClick,
          };
        },

        getTrendsChartTooltip: () => {
          return function (this: RetentionTrendsChart, x: number, _y: number, _series: any) {
            const row = this.state.segmentRows.find((r) => r.head === _series.options.name);
            const comparisonRow = this.state.comparisonSegmentRowsMapped.get(row.raw_head);
            const liftRow = this.state.liftSegmentRowsMapped.get(row.raw_head);

            const cellIndex = row.data.findIndex((cell) => cell.date.valueOf() === x);

            const cell = row.data[cellIndex];

            if (!cell) {
              // this case occurs when the retention bucket is changed:
              // there is an out-of-sync state that causes the cell lookup to fail
              return null;
            }

            const liftType = this._bookmarkParams.lift_comparison?.type;
            let liftBaselineRow;
            if (liftType === LiftType.Segment) {
              liftBaselineRow = this.state.segmentRows.find(
                (r) =>
                  r.raw_head ===
                  getLiftBaselineSegmentRawHead(row, this.state.segmentKeyToPath, this.bookmarkParams.lift_comparison),
              );
            }

            const sections = getToolTipSections(
              row,
              cellIndex,
              this._bookmarkParams,
              comparisonRow,
              liftRow,
              liftBaselineRow,
              this.state.segmentKeyToPath,
            );

            const footerText = cell.incomplete ? `footer-text="Incomplete ${this._bookmarkParams.date_unit}" ` : ``;

            const titleColorAttr = liftRow || !comparisonRow ? `title-color=${row.selection_color}` : ``;
            return `<mp-chart-tooltip ${titleColorAttr} ${footerText} sections="${htmlEncodeString(
              JSON.stringify(sections),
            )}">`;
          }.bind(this);
        },
      },
    };
  }

  handlePointClick = (point: Point) => {
    const {series, plotX, plotY} = point;
    const segmentName = series.options.name;

    if (segmentName === OTHER_SEGMENT_VALUE) {
      return;
    }

    const hasPopoverActions = this.popoverCallback?.call(null, segmentName).length;

    if (hasPopoverActions) {
      selectPoint(point);

      const left = series.chart.plotLeft + plotX + 12;
      const top = series.chart.plotTop + plotY + 12;
      this.update({
        popoverStyles: {left: `${left}px`, top: `${top}px`},
        popoverSelection: point,
      });
    }
  };

  handlePopoverAction(ev: CustomEvent) {
    const {series, x: dateTime} = this.state.popoverSelection;
    const displayName = series.options.name;
    const selectedSegmentIndex = this.state.segmentRows.findIndex((row) => row.head === displayName);
    const selectedSegment = this.state.segmentRows[selectedSegmentIndex];
    const matchingTrendIndex = selectedSegment.data.findIndex((bucket) => dateTime === bucket.date.getTime());

    this.dispatchEvent(
      new CustomEvent(`popoverAction`, {
        detail: {
          action: ev.detail.action,
          selectedRow: selectedSegment,
          colIdx: matchingTrendIndex,
          rowIdx: selectedSegmentIndex,
          color: selectedSegment.selection_color,
        },
      }),
    );

    this.handlePopoverClose();
  }

  handlePopoverClose() {
    const selectedPoint = this.state.popoverSelection;
    if (selectedPoint) {
      deselectPoint(selectedPoint);
    }
    this.update({
      popoverSelection: null,
      popoverStyles: null,
    });
  }

  getVisibleRow(idx: number) {
    let i = 0;
    let visibleIdx = 0;
    while (i < this.state.segmentRows.length) {
      const row = this.state.segmentRows[i];
      if (this.isRowSelected(row)) {
        if (visibleIdx === idx) {
          return row;
        } else {
          visibleIdx++;
        }
      }
      i++;
    }

    return null;
  }

  processData() {
    if (!(this._data && this._bookmarkParams && this.isConnected)) {
      return;
    }

    const groupBy = this._bookmarkParams.group_by;
    // TODO this is a hack to get the line chart and table to display proper date labels. They rely on Date construction
    // with implicit local browser time zone, like we were doing before. We should instead fix those components to make
    // them project timezone aware.
    const timezoneString = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || this.helpers.getProjectTimezone();
    const {segmentKeyToPath, metadata, data, compareToPastData, comparisonMetadata, liftData} = flattenQueryResponse(
      this._data,
      groupBy,
    );
    const {formattedUiRows, formattedComparisonRowsData, formattedLiftRowsData} = getUiRowsDataFromParsed(
      data,
      metadata,
      this._bookmarkParams,
      timezoneString,
      compareToPastData,
      comparisonMetadata,
      liftData,
    );
    this.update({
      segmentRows: formattedUiRows,
      comparisonSegmentRowsMapped: formattedComparisonRowsData,
      liftSegmentRowsMapped: formattedLiftRowsData,
      segmentKeyToPath,
    });

    const trendDataRows = mapToTrendDataRows({
      bookmark: this._bookmarkParams,
      numSegments: groupBy.length,
      formattedUiRows,
      segmentKeyToPath,
      formattedComparisonRowsData,
      formattedLiftRowsData,
    });

    const trendDataRowLookup = new Map(trendDataRows.map((row) => [row.rowId, row]));

    const segmentFilterItems = this.state.segmentRows.map((row) => {
      let baselineRow;
      if (this._bookmarkParams.lift_comparison?.type === LiftType.Segment) {
        const baselineRowKey = getLiftBaselineSegmentRawHead(
          row,
          segmentKeyToPath,
          this._bookmarkParams.lift_comparison,
        );
        baselineRow = this.state.segmentRows.find((r) => r.head === baselineRowKey);
      } else if (this._bookmarkParams.lift_comparison?.type === LiftType.Time) {
        baselineRow = this.state.comparisonSegmentRowsMapped.get(row.raw_head);
      }

      const comparisonValueGetter = getLiftComparisonValues(
        {
          segment: row.data,
          comparisonSegment: baselineRow?.data,
          liftType: this._bookmarkParams.lift_comparison?.type,
          projectTimezone: {
            timezone: timezoneString,
            utcOffset: this.helpers.getUtcOffset(),
          },
        },
        (segmentDates: RawCell[]): Record<string, number> =>
          segmentDates.reduce(
            (acc, datePoint) => ({
              ...acc,
              [new Date(datePoint.date).toISOString()]: this._bookmarkParams.percentage_view
                ? datePoint.percent
                : datePoint.value,
            }),
            {},
          ),
      );

      return {
        label: row.raw_head,
        id: row.raw_head,
        color: row.selection_color,
        tableRow: getTrendDataTableRow(trendDataRowLookup.get(row.raw_head), comparisonValueGetter),
      };
    });

    const tableColumns = getTrendDataTableColumnGroups({
      segments: getSegmentColumnSpecs(groupBy),
      row: trendDataRows[0],
      trendUnit: this._bookmarkParams.date_unit as TimeUnit,
      valueFormatter: (value, {isAverage}) => {
        if (this._bookmarkParams.percentage_view) {
          return formatPercent(value);
        } else {
          return formatDataTableNumber(value, {isAverage});
        }
      },
    });

    const maxNumSelections =
      this.attr(`segment-display-limit`) > 0
        ? this.attr(`segment-display-limit`)
        : REPORT_LIMITS[ReportName.Retention].segments[ChartType.RetentionTrend].max;
    this.segmentFilterController.loadSections([{items: segmentFilterItems, tableColumns}], {
      defaultNumSelections: REPORT_LIMITS[ReportName.Retention].segments[ChartType.RetentionTrend].default,
      maxNumSelections,
      tableSortConfig: this._bookmarkParams.sorting,
    });
    this.segmentFilterController.loadFromSavedLegend(
      this._bookmarkParams.selected_segments && [
        {selected: this._bookmarkParams.selected_segments.map((key) => ({key}))},
      ],
    );
    this.segmentFilterController.segmentationQueryLimit = REPORT_LIMITS[ReportName.Retention].query;
    this.segmentFilterController.exportSegmentationQueryLimit = REPORT_LIMITS[ReportName.Retention].csvExport;
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  connectedCallback() {
    super.connectedCallback();

    //TODO add store fetch
    this.processData();
  }

  getCurrentProject(): CurrentProject {
    return this.getContext(`currentProject`).get().value;
  }

  isRowSelected(row: FormattedRow) {
    return this.segmentFilterController.isItemSelected(0, row.raw_head);
  }
  getPercentSelected(rows: FormattedRow[]) {
    let visible = 0;
    for (const row of rows) {
      if (this.isRowSelected(row)) {
        visible++;
      }
    }

    return visible / rows.length;
  }

  set paramsAndResults(value: RetentionBookmarkParamsAndResults) {
    const updatedBookmarkParams = getBookmarkWithAppliedDefaults(value.params);
    const bookmarkChanged = !isEqual(this._bookmarkParams, updatedBookmarkParams);
    if (bookmarkChanged) {
      this._bookmarkParams = updatedBookmarkParams;
    }
    const dataChanged = !isEqual(this._data, value.results);
    if (dataChanged) {
      this._data = value.results;
    }
    if (bookmarkChanged || dataChanged) {
      this.processData();
    }
  }

  get bookmarkParams() {
    return this._bookmarkParams;
  }

  get data() {
    return this._data;
  }
}

export const RetentionTrendsChartEl = defineAndMakeJsxFactory<{
  props: RetentionTrendsChartProps;
  attrs?: RetentionTrendsChartAttrs;
}>(`mp-retention-trends-chart`, RetentionTrendsChart);

export const RetentionTrendsChartReact = panel2react<{
  props: RetentionTrendsChartProps;
  attrs?: RetentionTrendsChartAttrs;
}>(`mp-retention-trends-chart`, RetentionTrendsChart);
